import { closestCenter, DndContext } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { BlockWireframe } from '@octopus-do/octopus-project-core'
import { Wireframe } from '@octopus-do/octopus-storybook'
import { TimesSolid } from '@octopus-do/octopus-storybook/icons'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import cx from 'classnames'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import lightOrDark from '@/utils/color/lightOrDark'
import { useSortableWireframes } from './hooks/useSortableWireframes'
import css from './styles.module.scss'
import { removeWireframe, toCanvasTransform, wireframeItemId } from './utils'

interface FrameProps {
  children: React.ReactNode
  colorId: string
  completed: boolean
  editable: boolean
}

const Frame: React.FC<FrameProps> = ({
  children,
  colorId,
  completed,
  editable,
}) => {
  const colorHex = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )

  return (
    <div
      className={cx(css.container, {
        [css.completed]: completed,
        [css.light]: lightOrDark(colorHex) === 'light',
        [css.editable]: editable,
      })}
    >
      {children}
    </div>
  )
}

interface RowProps {
  wireframe: BlockWireframe
}

const Row: React.FC<RowProps> = ({ wireframe }) => (
  <div className={css.wireframe} id={wireframe}>
    <div className={css.drawing}>
      <Wireframe variant={wireframe} />
    </div>
  </div>
)

interface EditableRowProps extends RowProps {
  id: string
  // The cross was pressed: the row folds up.
  leaving: boolean
  // The row has folded up: time to drop it from the block.
  onLeft: () => void
  onRemove: () => void
  scale: number
  // Two or more rows: the row can be dragged along the list.
  sortable: boolean
}

// The cross keeps its press to itself: no drag starts from it, no focus
// moves to it, and the popover sees no interaction outside.
const onRemovePointerDown = (e: React.PointerEvent) => {
  e.preventDefault()
  e.stopPropagation()
}

// A row while the popover is open: dragged along the list, removed by the cross
// that shows on hover. The rows exist while the popover is open, and the
// popover dismisses on a pointerdown that reaches the document, so a press on
// a row goes to the drag sensor and no further. A removed row first folds up
// so that the rows below slide instead of jumping, and leaves the block when
// the fold has ended.
const EditableRow: React.FC<EditableRowProps> = ({
  id,
  leaving,
  onLeft,
  onRemove,
  scale,
  sortable,
  wireframe,
}) => {
  const { isDragging, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled: !sortable })

  const onPointerDown = (e: React.PointerEvent) => {
    listeners?.onPointerDown?.(e)
    e.stopPropagation()
  }

  // The fold transitions several properties; the track height ends it.
  const onTransitionEnd = (e: React.TransitionEvent) => {
    if (
      e.target === e.currentTarget &&
      e.propertyName === 'grid-template-rows'
    ) {
      onLeft()
    }
  }

  // The sorting transition covers the transform only and would override the
  // fold's own; the fold takes the row out of sorting anyway.
  const sortingTransition = leaving ? undefined : transition

  const style = {
    transform: CSS.Transform.toString(
      transform && toCanvasTransform(transform, scale)
    ),
    transition: sortingTransition,
  }

  return (
    <div
      className={cx(css.wireframe, {
        [css.draggable]: sortable,
        [css.dragging]: isDragging,
        [css.leaving]: leaving,
      })}
      id={wireframe}
      onPointerDown={onPointerDown}
      onTransitionEnd={onTransitionEnd}
      ref={setNodeRef}
      style={style}
    >
      <div className={css.drawing}>
        <Wireframe variant={wireframe} />
      </div>
      <button
        aria-label="Remove wireframe"
        className={css.remove}
        onClick={onRemove}
        onPointerDown={onRemovePointerDown}
        title="Remove"
        type="button"
      >
        <TimesSolid />
      </button>
    </div>
  )
}

interface Props {
  colorId: string
  wireframes: BlockWireframe[]
  completed?: boolean
}

// The wireframes of a block as drawn: nothing to grab.
export const Wireframes: React.FC<Props> = ({
  colorId,
  wireframes,
  completed = false,
}) => {
  if (wireframes.length === 0) {
    return null
  }

  return (
    <Frame colorId={colorId} completed={completed} editable={false}>
      {wireframes.map((wireframe, index) => (
        <Row key={wireframeItemId(wireframe, index)} wireframe={wireframe} />
      ))}
    </Frame>
  )
}

interface EditableProps extends Props {
  // The block's wireframes as a whole after a drop or a removal.
  onChange: (wireframes: BlockWireframe[]) => void
}

// The wireframes of a block whose wireframes popover is open: each row drags
// along the list and carries a cross to remove it, both hand the new set to
// the owner. The rows sit above the block's own drag layer, so the block
// itself is moved by its name or its empty space.
export const EditableWireframes: React.FC<EditableProps> = ({
  colorId,
  onChange,
  wireframes,
  completed = false,
}) => {
  const sorting = useSortableWireframes(wireframes, onChange)

  // The row folding up after its cross was pressed, by position.
  const [leaving, setLeaving] = useState<number | null>(null)

  if (wireframes.length === 0) {
    return null
  }

  const sortable = wireframes.length > 1

  const onLeft = (index: number) => {
    setLeaving(null)
    onChange(removeWireframe(wireframes, index))
  }

  // The context sits outside the frame: it renders its hidden accessibility
  // nodes after its children, and the rows' first/last margins rely on the
  // rows being the frame's only children.
  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={sorting.onDragEnd}
      onDragStart={sorting.onDragStart}
      sensors={sorting.sensors}
    >
      <SortableContext
        items={sorting.items}
        strategy={verticalListSortingStrategy}
      >
        <Frame colorId={colorId} completed={completed} editable>
          {wireframes.map((wireframe, index) => (
            <EditableRow
              id={sorting.items[index]}
              key={sorting.items[index]}
              leaving={leaving === index}
              onLeft={() => onLeft(index)}
              onRemove={() => setLeaving(index)}
              scale={sorting.scale}
              sortable={sortable}
              wireframe={wireframe}
            />
          ))}
        </Frame>
      </SortableContext>
    </DndContext>
  )
}
